import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { Keyboard } from "react-native"
import { usePathname } from "expo-router"

import type { AvatarMode } from "./AvatarModeContext"
import { useAvatarMode } from "./AvatarModeContext"
import { useChat } from "./ChatContext"
import { useVisemes } from "./VisemeContext"

const AUTO_MINIMIZE_DELAY_MS = 3000
// Grace period before we consider "no welcome flow" — avoids minimizing when we landed large and
// waitingForWelcomeMessage hasn't been set yet by ChatContext.
const WELCOME_FLOW_GRACE_MS = 5000

/**
 * MediaAvatarContext - Manages avatar sizing interactions with media playback and menus
 *
 * Rules:
 * 0. Avatar starts minimized. Large mode is set only when we're about to show the main screen for
 *    a "new channel" welcome: (a) setup/select-sponsor or change-channel calls setMode('large')
 *    before dismiss, or (b) single-channel first login (unauth/login) calls setMode('large') before
 *    router.replace("/"). We do not expand for role switch or sidekick transfer. When we're large
 *    on main (from any of those), we auto-minimize 3s after the welcome finishes speaking.
 * 1. When a menu screen opens -> if avatar is large or minimized, transition to tab (automatic)
 * 2. When a media screen opens -> if avatar is large, minimize (automatic)
 * 3. When media starts playing -> hide avatar (tab mode) and stop speech
 * 4. When media pauses -> restore to size before play (not remembered size)
 * 5. When media/menu closes -> restore to remembered size
 * 6. Manual transitions update remembered size, automatic transitions don't
 */

interface MediaAvatarContextType {
  /**
   * Call when navigating to a media player or menu route
   */
  onMediaOpen: () => void
  /**
   * Call when media starts playing (not paused)
   */
  onMediaPlay: () => void
  /**
   * Call when media is paused
   */
  onMediaPause: () => void
  /**
   * Call when media or menu closes (route unmounts)
   */
  onMediaClose: () => void
  /**
   * Call when user activates talk or type mode from media screen
   */
  onInteractionStart: () => void
  /**
   * Whether we're currently in a media route (not menu)
   */
  isInMediaRoute: boolean
}

const MediaAvatarContext = createContext<MediaAvatarContextType | undefined>(undefined)

const MEDIA_PLAYER_ROUTES = [
  "/video-player",
  "/youtube-player",
  "/audio-player",
  "/image-viewer",
  "/file-viewer",
  "/pdf-viewer",
  "/web-browser",
]

// Menu routes that should behave like media routes (auto-minimize large avatar, restore on close)
const MENU_ROUTES = [
  "/menu",
  "/content-library",
  "/change-role",
  "/change-channel",
  "/select-msk",
  "/member-profile",
  "/feedback",
  "/help",
  "/crisis-line",
  "/settings",
]

// Combined routes that trigger avatar minimization behavior
const OVERLAY_ROUTES = [...MEDIA_PLAYER_ROUTES, ...MENU_ROUTES]

export function MediaAvatarProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const { mode, setMode, getRememberedSize } = useAvatarMode()
  const { stopSpeech, isSpeaking } = useVisemes()
  const { waitingForWelcomeMessage } = useChat()
  const previousPathname = useRef(pathname)
  const sizeBeforeMedia = useRef<AvatarMode | null>(null)
  const sizeBeforePlay = useRef<AvatarMode | null>(null)

  // Stable ref for the current mode. Callbacks read this instead of closing over `mode`
  // directly, which would force them to be recreated on every mode change and trigger
  // cascading re-renders of all consumers (including the video player's handlePlayPause
  // effect, which re-runs on every dep change and causes the SpeechInputButton to remount).
  const modeRef = useRef(mode)
  modeRef.current = mode

  // Track whether we've already run the "initial auto-minimize" (3s after open or 3s after welcome spoken)
  const hasDoneInitialAutoMinimizeRef = useRef(false)
  const wasWaitingForWelcomeRef = useRef(false)
  const initialAutoMinimizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isInMediaRoute = MEDIA_PLAYER_ROUTES.some((route) => pathname.startsWith(route))

  // Track keyboard visibility so we don't restore the avatar while the user is typing.
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)
  useEffect(() => {
    const showSub = Keyboard.addListener("keyboardDidShow", () => setIsKeyboardVisible(true))
    const hideSub = Keyboard.addListener("keyboardDidHide", () => setIsKeyboardVisible(false))
    return () => {
      showSub.remove()
      hideSub.remove()
    }
  }, [])

  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`
  const isOnMainScreen = !OVERLAY_ROUTES.some((route) => normalizedPath.startsWith(route))

  // Initial auto-minimize: on main screen with large avatar, minimize after 3s if not in welcome flow.
  // Wait WELCOME_FLOW_GRACE_MS first so we don't minimize when we just landed from channel-select
  // and waitingForWelcomeMessage hasn't been set yet by ChatContext.
  useEffect(() => {
    if (!isOnMainScreen || mode !== "large" || hasDoneInitialAutoMinimizeRef.current) return
    if (waitingForWelcomeMessage) return
    if (wasWaitingForWelcomeRef.current) return

    initialAutoMinimizeTimeoutRef.current = setTimeout(() => {
      initialAutoMinimizeTimeoutRef.current = null
      // If waitingForWelcomeMessage became true, effect re-ran and cleared this timeout; no need to check again
      initialAutoMinimizeTimeoutRef.current = setTimeout(() => {
        initialAutoMinimizeTimeoutRef.current = null
        if (hasDoneInitialAutoMinimizeRef.current) return
        hasDoneInitialAutoMinimizeRef.current = true
        console.log("[MediaAvatarContext] Initial auto-minimize (3s after open)")
        setMode("minimized", false)
      }, AUTO_MINIMIZE_DELAY_MS)
    }, WELCOME_FLOW_GRACE_MS)

    return () => {
      if (initialAutoMinimizeTimeoutRef.current !== null) {
        clearTimeout(initialAutoMinimizeTimeoutRef.current)
        initialAutoMinimizeTimeoutRef.current = null
      }
    }
  }, [isOnMainScreen, mode, waitingForWelcomeMessage, setMode])

  // When waiting for welcome: minimize 3s after the welcome message finishes speaking
  useEffect(() => {
    if (waitingForWelcomeMessage) {
      wasWaitingForWelcomeRef.current = true
      return
    }
    if (!wasWaitingForWelcomeRef.current || isSpeaking) return
    if (!isOnMainScreen || mode !== "large" || hasDoneInitialAutoMinimizeRef.current) {
      wasWaitingForWelcomeRef.current = false
      return
    }

    wasWaitingForWelcomeRef.current = false
    hasDoneInitialAutoMinimizeRef.current = true
    const timeoutId = setTimeout(() => {
      console.log("[MediaAvatarContext] Auto-minimize 3s after welcome message finished speaking")
      setMode("minimized", false)
    }, AUTO_MINIMIZE_DELAY_MS)

    return () => clearTimeout(timeoutId)
  }, [waitingForWelcomeMessage, isSpeaking, isOnMainScreen, mode, setMode])

  // When navigating TO a media route from root (minimize if large; menu routes use onMenuOpen)
  const onMediaOpen = useCallback(() => {
    const currentMode = modeRef.current
    console.log("[MediaAvatarContext] onMediaOpen - current mode:", currentMode)

    // Save the remembered size before opening overlay (for restoring when overlay closes)
    sizeBeforeMedia.current = getRememberedSize()
    console.log("[MediaAvatarContext] Saved size before overlay:", sizeBeforeMedia.current)

    // If avatar is large, automatically minimize it (not a manual transition)
    if (currentMode === "large") {
      console.log("[MediaAvatarContext] Minimizing large avatar (automatic)")
      setMode("minimized", false) // false = automatic transition, doesn't update remembered size
      // Initialize sizeBeforePlay to minimized since we just transitioned to it
      sizeBeforePlay.current = "minimized"
    } else if (currentMode !== "tab") {
      // If already minimized or large (not tab), save it as sizeBeforePlay
      sizeBeforePlay.current = currentMode
    }
    console.log("[MediaAvatarContext] Initial sizeBeforePlay:", sizeBeforePlay.current)
  }, [setMode, getRememberedSize])

  // When navigating TO a menu route: transition to tab if avatar is minimized or large.
  // Save the current mode (not just remembered) so we restore what the user had when they
  // opened the menu (e.g. minimized after auto-minimize, even though remembered wasn't updated).
  const onMenuOpen = useCallback(() => {
    const currentMode = modeRef.current
    console.log("[MediaAvatarContext] onMenuOpen - current mode:", currentMode)
    const sizeToRestoreWhenClosed = currentMode !== "tab" ? currentMode : getRememberedSize()
    sizeBeforeMedia.current = sizeToRestoreWhenClosed
    console.log("[MediaAvatarContext] Saved size before menu:", sizeBeforeMedia.current)

    if (currentMode === "large" || currentMode === "minimized") {
      console.log("[MediaAvatarContext] Transitioning to tab for menu (automatic)")
      sizeBeforePlay.current = currentMode
      setMode("tab", false)
    }
    console.log("[MediaAvatarContext] sizeBeforePlay after menu open:", sizeBeforePlay.current)
  }, [setMode, getRememberedSize])

  // When media starts playing
  const onMediaPlay = useCallback(async () => {
    // Menu modal may be on top of the video; ignore play from underlying media so avatar stays tabbed
    const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`
    if (MENU_ROUTES.some((route) => normalizedPath.startsWith(route))) return

    const currentMode = modeRef.current
    console.log("[MediaAvatarContext] onMediaPlay - current mode before play:", currentMode)

    // Only save the mode if we're not already in tab mode
    // This prevents saving "tab" when rapidly playing/pausing
    if (currentMode !== "tab") {
      sizeBeforePlay.current = currentMode
      console.log("[MediaAvatarContext] Saved size before play:", sizeBeforePlay.current)
    } else {
      console.log(
        "[MediaAvatarContext] Already in tab mode, not overwriting sizeBeforePlay:",
        sizeBeforePlay.current,
      )
    }

    await stopSpeech()
    // Hide avatar to tab mode (automatic transition)
    setMode("tab", false)
  }, [pathname, stopSpeech, setMode])

  // When media is paused
  const onMediaPause = useCallback(() => {
    // Menu modal may be on top of the video; ignore pause from underlying media so avatar stays tabbed
    const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`
    if (MENU_ROUTES.some((route) => normalizedPath.startsWith(route))) return

    // Only restore when avatar is in tab (we hid it for playback). If user has expanded to large
    // or is in minimized, don't overwrite — e.g. tapping avatar to expand can trigger a pause event.
    if (modeRef.current !== "tab") return

    console.log("[MediaAvatarContext] onMediaPause - restoring to size before play")
    console.log("[MediaAvatarContext] sizeBeforePlay.current:", sizeBeforePlay.current)

    // Restore to the size we were at before playing (could be minimized or large if user manually changed it)
    // Default to minimized if sizeBeforePlay is somehow "tab" or null
    let sizeToRestore = sizeBeforePlay.current || "minimized"
    if (sizeToRestore === "tab") {
      console.log("[MediaAvatarContext] sizeBeforePlay was 'tab', defaulting to 'minimized'")
      sizeToRestore = "minimized"
    }

    console.log("[MediaAvatarContext] Restoring to:", sizeToRestore)
    setMode(sizeToRestore, false) // Automatic transition back
  }, [pathname, setMode])

  // When user activates talk or type mode from a media screen
  const onInteractionStart = useCallback(() => {
    // Only restore when avatar is in tab (we hid it for playback). If user already expanded
    // to large or is in minimized, leave mode as-is so pressing talk doesn't collapse the avatar.
    if (modeRef.current !== "tab") return

    console.log(
      "[MediaAvatarContext] onInteractionStart - restoring to size before play or remembered",
    )
    const sizeToRestore = sizeBeforePlay.current || sizeBeforeMedia.current || getRememberedSize()
    console.log("[MediaAvatarContext] Restoring to:", sizeToRestore)
    setMode(sizeToRestore, false) // Automatic transition back
  }, [setMode, getRememberedSize])

  // When media or menu closes (navigate away from overlay route)
  const onMediaClose = useCallback(() => {
    sizeBeforeMedia.current = null // Clear saved sizes
    sizeBeforePlay.current = null

    // Use current remembered size so if the user expanded to large (or minimized) while on the
    // media screen, we keep that; sizeBeforeMedia is not used so the avatar stays at whatever
    // size the user last set.
    const remembered = getRememberedSize()
    setMode(remembered, false) // Automatic transition back
  }, [setMode, getRememberedSize])

  // Auto-detect navigation to/from media/menu routes
  useEffect(() => {
    // Normalize pathname for consistent matching (expo-router can report with or without leading slash)
    const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`

    const wasInOverlayRoute = OVERLAY_ROUTES.some((route) =>
      previousPathname.current.startsWith(route),
    )
    const wasInMenuRoute = MENU_ROUTES.some((route) => previousPathname.current.startsWith(route))
    const isNowInOverlayRoute = OVERLAY_ROUTES.some((route) => normalizedPath.startsWith(route))
    const isNowInMenuRoute = MENU_ROUTES.some((route) => normalizedPath.startsWith(route))

    // Navigating TO an overlay route (and wasn't already in one)
    if (!wasInOverlayRoute && isNowInOverlayRoute) {
      if (isNowInMenuRoute) {
        onMenuOpen()
      } else {
        onMediaOpen()
      }
    }

    // Navigating from a non-menu overlay (e.g. media) INTO a menu — transition avatar to tab
    if (wasInOverlayRoute && !wasInMenuRoute && isNowInMenuRoute) {
      onMenuOpen()
    }

    // Navigating AWAY from an overlay route
    if (wasInOverlayRoute && !isNowInOverlayRoute) {
      onMediaClose()
    } else if (
      // Fallback: we're on main screen but avatar is still in tab (e.g. (app) remounted after role
      // switch so we never saw "leaving overlay"). Restore to remembered size.
      // Skip while the keyboard is visible — the user may be typing and the cluster expansion
      // legitimately sets tab mode; restoring here would cause an avatar toggle loop.
      !isNowInOverlayRoute &&
      mode === "tab" &&
      !isKeyboardVisible
    ) {
      onMediaClose()
    }

    previousPathname.current = normalizedPath
  }, [pathname, mode, isKeyboardVisible, onMenuOpen, onMediaOpen, onMediaClose, getRememberedSize])

  const value = useMemo(
    () => ({
      onMediaOpen,
      onMediaPlay,
      onMediaPause,
      onMediaClose,
      onInteractionStart,
      isInMediaRoute,
    }),
    [onMediaOpen, onMediaPlay, onMediaPause, onMediaClose, onInteractionStart, isInMediaRoute],
  )

  return <MediaAvatarContext.Provider value={value}>{children}</MediaAvatarContext.Provider>
}

export function useMediaAvatar() {
  const context = useContext(MediaAvatarContext)
  if (context === undefined) {
    throw new Error("useMediaAvatar must be used within a MediaAvatarProvider")
  }
  return context
}

export function useOptionalMediaAvatar() {
  return useContext(MediaAvatarContext)
}
